import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Modal, Button, Select, MenuItem, CircularProgress, Checkbox } from "@mui/material";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { useAddTransmissionMutation, useGetCategoriesQuery, useUpdateTransmissionMutation } from "../service/features/api";
import { VEHICLE_TYPE } from "../constants/enum";
import { getVehicleTypeLabel } from "../utils/vehicle";
import { toast } from "react-toastify";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  transmissionData?: any;
}

export default function AddTransmission({
  open,
  onClose,
  setOpen,
  transmissionData
}: ModalProps) {

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState<number | "">("");
  const [name, setName] = useState("");
  const [errors, setErrors] = useState<any>({});

  // Queries
  const { data: categoriesData } = useGetCategoriesQuery(
    { page: 1, limit: 100, type: selectedVehicleType },
    { skip: !selectedVehicleType }
  );
  const [addTransmission, { isLoading: isAdding }] = useAddTransmissionMutation();
  const [updateTransmission, { isLoading: isUpdating }] = useUpdateTransmissionMutation();

  useEffect(() => {
    if (transmissionData && open) {
      setName(transmissionData.name || "");
      let catIds: string[] = [];
      if (transmissionData.category && Array.isArray(transmissionData.category) && transmissionData.category.length > 0) {
        catIds = transmissionData.category.map((c: any) => c._id);
      } else if (transmissionData.categoryId) {
        catIds = Array.isArray(transmissionData.categoryId) ? transmissionData.categoryId : [transmissionData.categoryId];
      }
      setSelectedCategories(catIds);
      const vType = transmissionData.type;
      setSelectedVehicleType(Array.isArray(vType) ? vType[0] : (vType || ""));
      setErrors({});
    } else if (!transmissionData && open) {
      // Reset form
      setName("");
      setSelectedCategories([]);
      setSelectedVehicleType("");
      setErrors({});
    }
  }, [transmissionData, open]);

  const handleCategoryChange = (event: any) => {
    const value = event.target.value;
    const categories = categoriesData?.data?.data || [];

    if (value.includes("all")) {
      if (selectedCategories.length === categories.length) {
        setSelectedCategories([]);
      } else {
        setSelectedCategories(categories.map((cat: any) => cat._id));
      }
    } else {
      setSelectedCategories(typeof value === 'string' ? (value as string).split(',') : value);
    }

    if (errors.category) setErrors({ ...errors, category: "" });
  };

  const handleVehicleTypeChange = (event: any) => {
    const value = event.target.value as number;
    setSelectedVehicleType(value);
    setSelectedCategories([]); // Reset categories when vehicle type changes
    if (errors.vehicleType) setErrors({ ...errors, vehicleType: "" });
  };

  const handleNameChange = (e: any) => {
    if (e.target.value.length <= 40) {
      setName(e.target.value);
      if (errors.name) setErrors({ ...errors, name: "" });
    }
  };

  const handleValidation = () => {
    let tempErrors: any = {};
    let isValid = true;

    if (!name.trim()) {
      tempErrors.name = "Transmission Type is required / نوع ناقل الحركة مطلوب";
      isValid = false;
    }
    if (selectedCategories.length === 0) {
      tempErrors.category = "At least one category is required / مطلوب فئة واحدة على الأقل";
      isValid = false;
    }
    if (!selectedVehicleType) {
      tempErrors.vehicleType = "Vehicle type is required / نوع المركبة مطلوب";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!handleValidation()) return;

    const payload = {
      name,
      categoryId: selectedCategories,
      type: Number(selectedVehicleType),
    };

    try {
      if (transmissionData?._id) {
        await updateTransmission({ id: transmissionData._id, ...payload }).unwrap();
        toast.success("Transmission updated successfully / تم تحديث ناقل الحركة بنجاح");
      } else {
        await addTransmission(payload).unwrap();
        toast.success("Transmission added successfully / تمت إضافة ناقل الحركة بنجاح");
      }
      setOpen(false);
    } catch (err: any) {
      toast.error(err?.data?.message || "Something went wrong / حدث خطأ ما");
    }
  };

  return (
    <Modal
      className="modal rejectReason_modal"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{transmissionData ? "Edit Transmission / تعديل ناقل الحركة" : "Add Transmission / إضافة ناقل حركة"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => setOpen(false)}
              aria-label="Close / إغلاق"
            >
              <CloseIcon />
            </button>
          </div>

          <form className="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <div className="control_group">
              <label htmlFor="transmission-name">Transmission Type / نوع ناقل الحركة</label>
              <TextField
                id="transmission-name"
                placeholder="Enter Type (e.g., Manual, Automatic) / أدخل النوع (مثال: يدوي، أوتوماتيكي)"
                fullWidth
                hiddenLabel
                value={name}
                onChange={handleNameChange}
                error={!!errors.name}
                helperText={errors.name}
                inputProps={{ maxLength: 40 }}
              />
            </div>

            <div className="control_group">
              <label htmlFor="VehicleType">Vehicle Type / نوع المركبة</label>
              <Select
                fullWidth
                hiddenLabel
                displayEmpty
                value={selectedVehicleType}
                onChange={handleVehicleTypeChange}
                renderValue={(selected) => {
                  if (!selected) return "Select Vehicle Type / اختر نوع المركبة";
                  return getVehicleTypeLabel(selected);
                }}
                error={!!errors.vehicleType}
              >
                <MenuItem value="" disabled>Select Vehicle Type / اختر نوع المركبة</MenuItem>
                {Object.keys(VEHICLE_TYPE)
                  .filter(k => isNaN(Number(k)))
                  .map((key) => (
                    <MenuItem key={key} value={(VEHICLE_TYPE as any)[key]}>
                      {getVehicleTypeLabel((VEHICLE_TYPE as any)[key])}
                    </MenuItem>
                  ))}
              </Select>
              {errors.vehicleType && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.vehicleType}</div>}
            </div>

            <div className="control_group">
              <label htmlFor="Category">Category / الفئة</label>
              <Select
                multiple
                fullWidth
                hiddenLabel
                value={selectedCategories}
                onChange={handleCategoryChange}
                renderValue={(selected) => {
                  const selectedIds = (selected as string[]) || [];
                  if (selectedIds.length === 0) return selectedVehicleType ? "Select Categories / اختر الفئات" : "Select Vehicle Type First / اختر نوع المركبة أولاً";

                  return selectedIds.map(id => {
                    // 1. Try to find in loaded categories list
                    const name = categoriesData?.data?.data?.find((cat: any) => cat._id === id)?.name;
                    if (name) return name;

                    // 2. Try to find in transmissionData.category (array of objects)
                    if (transmissionData?.category && Array.isArray(transmissionData.category)) {
                      const initialCat = transmissionData.category.find((cat: any) => cat._id === id);
                      if (initialCat?.name) return initialCat.name;
                    }

                    // 3. Try to use categoryName by index matching
                    if (transmissionData?.categoryId && transmissionData?.categoryName) {
                      if (Array.isArray(transmissionData.categoryId) && Array.isArray(transmissionData.categoryName)) {
                        const index = transmissionData.categoryId.indexOf(id);
                        if (index !== -1 && transmissionData.categoryName[index]) return transmissionData.categoryName[index];
                      } else if (transmissionData.categoryId === id) {
                        return Array.isArray(transmissionData.categoryName) ? transmissionData.categoryName[0] : transmissionData.categoryName;
                      }
                    }

                    return id;
                  }).join(", ");
                }}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 250,
                    },
                  },
                }}
                disabled={!selectedVehicleType}
                error={!!errors.category}
              >
                <MenuItem value="" disabled>
                  {selectedVehicleType ? "Select Categories / اختر الفئات" : "Select Vehicle Type First / اختر نوع المركبة أولاً"}
                </MenuItem>
                {categoriesData?.data?.data?.length > 0 && (
                  <MenuItem value="all">
                    <em>{selectedCategories.length === categoriesData.data.data.length ? "Deselect All / إلغاء تحديد الكل" : "Select All / تحديد الكل"}</em>
                  </MenuItem>
                )}
                {categoriesData?.data?.data?.map((category: any) => (
                  <MenuItem key={category._id} value={category._id}>
                    <Checkbox checked={selectedCategories.includes(category._id)} />
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.category && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.category}</div>}
            </div>

            <div className="footer_btn_flex">
              <Button
                type="submit"
                className="btn btn_primary"
                disabled={isAdding || isUpdating}
              >
                {isAdding || isUpdating ? <CircularProgress size={20} color="inherit" /> : "Save / حفظ"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
